"use client";

import Link from "next/link";
import { MessageCircle, Plus, ShoppingCart } from "lucide-react";

import { Button } from "@/components/ui/button";
import { HeaderAccountMenu } from "@/components/feed/header-account-menu";
import { useCartContext } from "@/components/cart/cart-provider";
import { useCartCalculations } from "@/hooks/use-cart-calculations";
import { useOverlay } from "@/components/context/overlay-context";
import { cn } from "@/lib/utils";

/**
 * HeaderActions — the shared right-side header cluster (mockup header). Used by
 * BOTH the feed header (FeedTopBar) and the global non-feed header
 * (FeedSearchHeader) so navigation is uniform app-wide and the deprecated right
 * SidebarNav can be retired.
 *
 * Logged-out: Log In · Get Started (foil) · Cart. Logged-in: Messages · Cart ·
 * Sell · Account. `onDark` styles the icon buttons for a dark/immersive header.
 *
 * Sell/Cart drive the same `OverlayContext` (`openPanel`/`openUtility`) as the
 * mobile bottom nav — NOT a `[data-cart-button]` DOM lookup or an `open-cart`
 * window event, both dead since the overlay-FSM migration (they used to be the
 * live mechanism; cart.tsx no longer listens for them).
 *
 * Cart and Sell carry `data-testid="cart-trigger"` / `"create-listing-trigger"`,
 * the viewport-agnostic selectors e2e clicks. Both mounts of this cluster are
 * `hidden md:flex` and mutually exclusive (FeedTopBar on /feed, FeedSearchHeader
 * everywhere else), so on desktop these are the ONE visible trigger of each kind
 * — the right SidebarNav that used to carry both was retired on 2026-07-29,
 * which left the desktop viewport with no match for either and no way to reach
 * the cart drawer or the creation flow from a spec. The mobile counterparts stay
 * in the `md:hidden` bottom nav.
 */
export function HeaderActions({
  isLoggedIn,
  onDark = false,
  className,
}: {
  isLoggedIn: boolean;
  onDark?: boolean;
  className?: string;
}) {
  const { cartItems } = useCartContext();
  const { uniqueItemCount } = useCartCalculations(cartItems);
  const { openPanel, openUtility } = useOverlay();

  const openCart = () => openUtility({ kind: "cart" });
  const openCreation = () => openPanel({ kind: "creation" });

  const iconBtn = cn(
    "flex size-10 items-center justify-center rounded-full transition-colors",
    onDark ? "text-white hover:bg-white/15" : "text-foreground hover:bg-accent",
  );

  const cart = (
    <button
      type="button"
      aria-label="Cart"
      data-testid="cart-trigger"
      onClick={openCart}
      className={cn(iconBtn, "relative")}
    >
      <ShoppingCart className="size-[21px]" strokeWidth={2} />
      {uniqueItemCount > 0 ? (
        <span className="absolute -right-0.5 -top-0.5 flex min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-[18px] text-primary-foreground ring-2 ring-background">
          {uniqueItemCount > 99 ? "99+" : uniqueItemCount}
        </span>
      ) : null}
    </button>
  );

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {isLoggedIn ? (
        <>
          <Link href="/chat" aria-label="Messages" className={iconBtn}>
            <MessageCircle className="size-[21px]" strokeWidth={2} />
          </Link>
          {cart}
          <Button
            type="button"
            onClick={openCreation}
            data-testid="create-listing-trigger"
            className="h-10 rounded-full px-4 text-sm"
          >
            <Plus className="mr-1.5 size-4" />
            Sell
          </Button>
          <HeaderAccountMenu triggerClassName={iconBtn} />
        </>
      ) : (
        <>
          <Button asChild variant="outline-solid" className="h-10 rounded-full px-4 text-sm">
            <Link href="/login">Log In</Link>
          </Button>
          <Button asChild variant="foil" className="h-10 rounded-full px-5 text-sm">
            <Link href="/register">Get Started</Link>
          </Button>
          {cart}
        </>
      )}
    </div>
  );
}
